import { useState } from 'react';
import { Modal, Button, Badge } from '@react-apps/design-system';
import {
  useCorrelatedEntityEvents,
  type CorrelatedEventModule,
} from '../hooks/use-correlated-entity-events';
import { EventRow } from '../shared/transaction-event-row';

const PAGE_SIZE = 10;

const MODULE_LABEL: Record<CorrelatedEventModule, string> = {
  sales: 'Sales',
  identity: 'Identity',
};

/**
 * O que uma row do webhook inbox gerou no sistema: os `entity_event` de Sales
 * e Identity carimbados com o id da row, intercalados por data.
 */
export function CorrelatedEntityEventsModal({
  correlationId,
  open,
  onClose,
}: {
  correlationId: string | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const { events, total, hasMore, isLoading, isFetching, failedModules } =
    useCorrelatedEntityEvents(open ? correlationId : undefined, perPage);

  const handleClose = () => {
    setPerPage(PAGE_SIZE);
    onClose();
  };

  const allFailed = failedModules.length === 2;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Eventos correlacionados"
      description="O que este webhook gerou em Sales e Identity, dos mais recentes aos mais antigos."
      size="md"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-(--color-text-secondary) text-xs">
            {!isLoading && !allFailed && (
              <>
                {events.length} de {total} {total === 1 ? 'evento' : 'eventos'}
              </>
            )}
          </span>
          <Button
            onClick={() => setPerPage(perPage + PAGE_SIZE)}
            disabled={!hasMore || isFetching}
          >
            Ver mais
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <ul className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <li
              key={i}
              className="border-(--color-border) bg-(--color-bg-secondary) h-20 animate-pulse rounded-lg border"
            />
          ))}
        </ul>
      ) : allFailed ? (
        <p className="text-(--color-text-secondary) py-8 text-center text-sm">
          Não foi possível carregar os eventos.
        </p>
      ) : (
        <div className="space-y-3">
          {failedModules.length > 0 && (
            <p className="text-(--color-text-secondary) text-xs">
              Não foi possível carregar os eventos de{' '}
              {failedModules.map((m) => MODULE_LABEL[m]).join(', ')} — você pode
              não ter acesso a esse módulo.
            </p>
          )}
          {events.length === 0 ? (
            <p className="text-(--color-text-secondary) py-8 text-center text-sm">
              Nenhum evento gerado por este webhook.
            </p>
          ) : (
            <ol>
              {events.map((event, i) => (
                <EventRow
                  key={`${event.module}-${event.id}`}
                  event={event}
                  isLast={i === events.length - 1}
                  badge={
                    <Badge
                      variant={event.module === 'sales' ? 'info' : 'default'}
                    >
                      {MODULE_LABEL[event.module]} · {event.entityType}
                    </Badge>
                  }
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </Modal>
  );
}
